import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { Org, OrgPreferences, ModulePrefixes, QrSettings } from '@cmms/types'
import { api } from '../lib/api'

const DEFAULT_PREFIXES: Required<ModulePrefixes> = {
  asset:      'AST',
  part:       'PRT',
  work_order: 'WO',
  location:   'LOC',
  vendor:     'VND',
  request:    'REQ',
}

const DEFAULT_QR: Required<QrSettings> = {
  company_name:     '',
  tagline:          '',
  footer_text:      '',
  logo_url:         null,
  fg_color:         '#111827',
  bg_color:         '#FFFFFF',
  error_correction: 'M',
}

const DEFAULTS: Required<OrgPreferences> = {
  currency:             'USD',
  currencySymbol:       '$',
  dateFormat:           'MM/DD/YYYY',
  timeFormat:           '12h',
  timezone:             Intl.DateTimeFormat().resolvedOptions().timeZone,
  language:             'en',
  modulePrefixes:       DEFAULT_PREFIXES,
  wo_geofence_enabled:  false,
  wo_geofence_radius_m: 100,
  qr:                   DEFAULT_QR,
  // Blank object → useSiteFieldLabels() falls back to "Custom field N"
  // for every slot. Filled per-org via Settings → Prefixes.
  siteFieldLabels:      {},
}

/**
 * Returns the org's resolved preferences, merged over sensible defaults.
 * Also exposes the raw org object + helpers for formatting values.
 *
 * Safe to call from any authenticated component — uses the same query key
 * that the Settings page writes to, so updates propagate instantly.
 */
export function useOrgSettings() {
  const orgQ = useQuery({
    queryKey: ['org'],
    queryFn: () => api.orgs.getCurrent(),
    staleTime: 5 * 60 * 1000,
    retry: 2,
  })

  const org: Org | null = orgQ.data ?? null

  const prefs = useMemo<Required<OrgPreferences>>(() => ({
    ...DEFAULTS,
    ...(org?.preferences ?? {}),
  }), [org?.preferences])

  const prefixes = useMemo<Required<ModulePrefixes>>(() => ({
    ...DEFAULT_PREFIXES,
    ...(org?.preferences?.modulePrefixes ?? {}),
  }), [org?.preferences?.modulePrefixes])

  return {
    org,
    prefs,
    prefixes,
    loading: orgQ.isLoading,

    /** Format a number as currency using the org's currency setting. */
    formatCurrency: (value: number) => {
      try {
        return new Intl.NumberFormat(prefs.language, {
          style: 'currency',
          currency: prefs.currency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }).format(value)
      } catch {
        return `${prefs.currencySymbol}${value.toLocaleString()}`
      }
    },

    /** Format a Date using the org's date/time preferences. Respects
     *  `dateFormat` (MM/DD/YYYY | DD/MM/YYYY | YYYY-MM-DD | DD MMM YYYY)
     *  and `timeFormat` (12h | 24h), and applies the org timezone
     *  before pulling components so dates render consistently across
     *  every browser tz.
     *
     *  We can't use Intl.DateTimeFormat's locale-driven layout for the
     *  date portion because it picks "Jan 15, 2026"-style output and
     *  ignores explicit pattern hints — so we extract the parts and
     *  re-assemble them ourselves based on the saved preference. */
    formatDate: (d: Date | string, includeTime = false) => {
      const date = typeof d === 'string' ? new Date(d) : d
      if (Number.isNaN(date.getTime())) return ''
      try {
        // Use en-CA for the parts extraction because it reliably
        // returns ISO-style numeric tokens (year/month/day with 2-digit
        // zero padding) — that gives us clean components to rearrange.
        const parts = new Intl.DateTimeFormat('en-CA', {
          timeZone: prefs.timezone,
          year: 'numeric', month: '2-digit', day: '2-digit',
        }).formatToParts(date)
        const get = (t: string) => parts.find(p => p.type === t)?.value ?? ''
        const Y = get('year'), M = get('month'), D = get('day')
        let datePart: string
        if (prefs.dateFormat === 'DD MMM YYYY') {
          // Pull the abbreviated month name from a separate Intl call
          // so we don't have to keep a month-name table in sync. en-US
          // gives the conventional "Jan / Feb / Mar" abbreviations.
          const monShort = new Intl.DateTimeFormat('en-US', {
            timeZone: prefs.timezone, month: 'short',
          }).format(date)
          datePart = `${D} ${monShort} ${Y}`
        } else if (prefs.dateFormat === 'DD/MM/YYYY') {
          datePart = `${D}/${M}/${Y}`
        } else if (prefs.dateFormat === 'YYYY-MM-DD') {
          datePart = `${Y}-${M}-${D}`
        } else /* MM/DD/YYYY (default) */ {
          datePart = `${M}/${D}/${Y}`
        }
        if (!includeTime) return datePart
        const timePart = new Intl.DateTimeFormat('en-US', {
          timeZone: prefs.timezone,
          hour: 'numeric',
          minute: '2-digit',
          hour12: prefs.timeFormat === '12h',
        }).format(date)
        return `${datePart} ${timePart}`
      } catch {
        return date.toLocaleDateString()
      }
    },

    /** Short month/day format for compact chip displays (no year).
     *  Mirrors the user's `dateFormat` order:
     *    • `MM/DD/YYYY`   → `01/15`
     *    • `DD/MM/YYYY`   → `15/01`
     *    • `YYYY-MM-DD`   → `01-15`
     *    • `DD MMM YYYY`  → `15 Jan`
     *  Keeps the separators consistent with `formatDate` so a chip
     *  that expands to a full date doesn't change punctuation. */
    formatDateShort: (d: Date | string) => {
      const date = typeof d === 'string' ? new Date(d) : d
      if (Number.isNaN(date.getTime())) return ''
      try {
        const parts = new Intl.DateTimeFormat('en-CA', {
          timeZone: prefs.timezone,
          month: '2-digit', day: '2-digit',
        }).formatToParts(date)
        const get = (t: string) => parts.find(p => p.type === t)?.value ?? ''
        const M = get('month'), D = get('day')
        if (prefs.dateFormat === 'DD MMM YYYY') {
          const monShort = new Intl.DateTimeFormat('en-US', {
            timeZone: prefs.timezone, month: 'short',
          }).format(date)
          return `${D} ${monShort}`
        }
        return prefs.dateFormat === 'DD/MM/YYYY' ? `${D}/${M}` :
               prefs.dateFormat === 'YYYY-MM-DD' ? `${M}-${D}` :
                                                   `${M}/${D}`
      } catch {
        return date.toLocaleDateString()
      }
    },

    /** Format a Date to show only time. */
    formatTime: (d: Date | string) => {
      const date = typeof d === 'string' ? new Date(d) : d
      try {
        return new Intl.DateTimeFormat(prefs.language, {
          timeZone: prefs.timezone,
          hour: 'numeric',
          minute: '2-digit',
          hour12: prefs.timeFormat === '12h',
        }).format(date)
      } catch {
        return date.toLocaleTimeString()
      }
    },
  }
}
